import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { OverlayScrollArea } from '@odo/shared-ui';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { useDmMessages } from '../../../../hooks/useDmMessages';
import { useUser } from '../../../../providers/UserProvider';
import { listMessages } from '../../../../services/dmMessages';
import { DmShellLayout } from './DmShellLayout';
import { DmMessageBubble } from './DmMessageBubble';
import { BlockedNotLinkedBanner, ErrorBanner } from './DmBanners';
import { AiReplyInThread } from './aiDraft/AiReplyInThread';
import { isAnsweredByUs } from './aiDraft/answeredByUs';
import { DmMessageComposer, type DmComposerHandle } from './composer/DmMessageComposer';
import { useComposerAttachment } from './composer/useComposerAttachment';
import { useDmMessageSend } from './composer/useDmMessageSend';
import type { DisplayMessage } from './composer/pendingMessages';
import { isNearBottom } from './threadScroll';
import type { EmbeddableActionRunnerProps } from '../types';
import type { AiReplySource } from '../../../monitoring/messages/triage/aiTriageState';
import type { DmMessage } from '../../../../services/dmMessages';

function formatDateLabel(isoString: string): string {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (isSameDay(date, today)) return 'Today';
    if (isSameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function groupMessagesByDay(
    messages: DisplayMessage[]
): Array<{ label: string; messages: DisplayMessage[] }> {
    const groups: Array<{ label: string; messages: DisplayMessage[] }> = [];
    let currentLabel = '';

    for (const msg of messages) {
        const label = formatDateLabel(msg.createdAt);
        if (label !== currentLabel) {
            currentLabel = label;
            groups.push({ label, messages: [] });
        }
        groups[groups.length - 1].messages.push(msg);
    }

    return groups;
}

interface PatientMessagesLandingProps extends EmbeddableActionRunnerProps {
    // Called after a send succeeds, for hosts that list the patient's unreviewed messages.
    onMessageSent?: () => void;
    // The AI's reply suggestion, shown after the newest message; hosts without triage omit it.
    aiReply?: AiReplySource;
    // Rendered in the back-header's right slot, e.g. a mark-as-read button.
    headerRight?: ReactNode;
}

// Keyed by patient so a staged file, draft or failed bubble never carries over to another patient.
function PatientMessagesLanding(props: PatientMessagesLandingProps) {
    return <PatientMessagesThread key={props.patientId} {...props} />;
}

function PatientMessagesThread({
    patientId,
    onBack,
    embedded = false,
    onMessageSent,
    aiReply,
    headerRight
}: PatientMessagesLandingProps) {
    const { data: card, isLoading: cardLoading } = usePatientCard(patientId);
    const { user } = useUser();
    const currentUserName = user?.name ?? 'You';
    const {
        data: messagesData,
        isLoading: messagesLoading,
        isRefreshing,
        error: messagesError,
        refetch
    } = useDmMessages(patientId);
    const queryClient = useQueryClient();

    const isLinked = card?.monitoring?.dmId != null;
    const isLoading = cardLoading || (isLinked && messagesLoading);

    const [olderCursor, setOlderCursor] = useState<string | null>(null);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const prevScrollHeightRef = useRef<number>(0);
    // Where the reader last left the thread; content growing below doesn't change it.
    const nearBottomRef = useRef(true);

    const composerRef = useRef<DmComposerHandle>(null);
    // Reads the ref on call: the composer mounts after the thread's first render.
    const composer = useMemo<DmComposerHandle>(
        () => ({
            readText: () => composerRef.current?.readText() ?? '',
            replaceText: (text: string) => composerRef.current?.replaceText(text)
        }),
        []
    );
    const attachment = useComposerAttachment();
    const { pendingMessages, send, retry, isSending } = useDmMessageSend({
        patientId,
        patientName: card?.name ?? null,
        senderName: currentUserName,
        refetchThread: refetch,
        onSent: onMessageSent
    });

    const allMessages: DisplayMessage[] = [...(messagesData?.messages ?? []), ...pendingMessages];
    // A reply sent from DM directly settles it before the inbox catches up; without the
    // thread we can't tell whether we already replied.
    const shownAiReply =
        aiReply && !messagesError && !isAnsweredByUs(allMessages) ? aiReply : undefined;

    const keepEndInView = useCallback(() => {
        const el = scrollRef.current;
        if (el && nearBottomRef.current) el.scrollTop = el.scrollHeight;
    }, []);

    useEffect(() => {
        if (messagesData?.nextCursor) {
            setOlderCursor(messagesData.nextCursor);
        }
    }, [messagesData?.nextCursor]);

    useEffect(() => {
        if (!isLoadingOlder && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
        // Not on isLoadingOlder: finishing an older page must keep its restored scroll position.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messagesData, pendingMessages.length]);

    const handleLoadOlder = useCallback(async () => {
        if (!olderCursor || isLoadingOlder) return;
        setIsLoadingOlder(true);
        if (scrollRef.current) {
            prevScrollHeightRef.current = scrollRef.current.scrollHeight;
        }
        try {
            const result = await listMessages(patientId, { cursor: olderCursor });
            setOlderCursor(result.nextCursor);
            await queryClient.setQueryData(
                ['v2/patients', patientId, 'dm', 'messages'],
                (prev: { messages: DmMessage[]; nextCursor: string | null } | undefined) => ({
                    messages: [...result.messages, ...(prev?.messages ?? [])],
                    nextCursor: prev?.nextCursor ?? null
                })
            );
            if (scrollRef.current) {
                scrollRef.current.scrollTop =
                    scrollRef.current.scrollHeight - prevScrollHeightRef.current;
            }
        } finally {
            setIsLoadingOlder(false);
        }
    }, [olderCursor, isLoadingOlder, patientId, queryClient]);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current) return;
        nearBottomRef.current = isNearBottom(scrollRef.current);
        if (scrollRef.current.scrollTop === 0 && olderCursor && !isLoadingOlder) {
            void handleLoadOlder();
        }
    }, [olderCursor, isLoadingOlder, handleLoadOlder]);

    // The shared scroll area owns the scroll element, so listen on it once the thread is mounted.
    const threadMounted = !isLoading && isLinked;
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !threadMounted) return;
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll, threadMounted]);

    const dayGroups = groupMessagesByDay(allMessages);
    const hasMessages = allMessages.length > 0;

    const listErrorMessage = messagesError instanceof Error ? messagesError.message : null;

    return (
        <DmShellLayout
            title="Patient messages"
            onBack={onBack}
            embedded={embedded}
            headerRight={headerRight}
        >
            <div className="w-full flex flex-col h-full gap-4">
                {isLoading && <LoadingSkeleton />}

                {!isLoading && !isLinked && <BlockedNotLinkedBanner onBack={onBack} />}

                {!isLoading && isLinked && listErrorMessage && (
                    <ErrorBanner message={listErrorMessage} onRetry={() => void refetch()} />
                )}

                {!isLoading && isLinked && (
                    <div className="relative flex flex-col flex-1 min-h-0">
                        {isRefreshing && <RefreshingBadge />}
                        {/* Thread */}
                        <OverlayScrollArea
                            scrollRef={scrollRef}
                            className="flex-1 min-h-0"
                            contentClassName="py-2 pr-3"
                            innerClassName="min-h-full flex flex-col gap-3"
                            trackInsetPx={6}
                        >
                            {isLoadingOlder && (
                                <div className="flex justify-center py-2">
                                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                                </div>
                            )}

                            {!hasMessages && (
                                <div className="flex flex-1 items-center justify-center">
                                    <span className="text-sm text-gray-400">
                                        No messages yet. Send the first one below.
                                    </span>
                                </div>
                            )}

                            {hasMessages &&
                                dayGroups.map((group) => (
                                    <div key={group.label} className="flex flex-col gap-3">
                                        <div className="text-[11px] text-gray-400 text-center my-2 font-medium uppercase tracking-wide">
                                            {group.label}
                                        </div>
                                        {group.messages.map((msg) => (
                                            <DmMessageBubble
                                                key={msg.id}
                                                message={msg}
                                                onRetry={retry}
                                                retryDisabled={isSending}
                                            />
                                        ))}
                                    </div>
                                ))}

                            {shownAiReply && (
                                <AiReplyInThread
                                    source={shownAiReply}
                                    composer={composer}
                                    onContentChange={keepEndInView}
                                />
                            )}
                        </OverlayScrollArea>

                        <DmMessageComposer
                            ref={composerRef}
                            patientId={patientId}
                            attachment={attachment}
                            isSending={isSending}
                            onSend={send}
                        />
                    </div>
                )}
            </div>
        </DmShellLayout>
    );
}

// Cached messages show at once; this marks the background fetch for newer ones.
function RefreshingBadge() {
    return (
        <div
            role="status"
            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs text-gray-500 shadow-sm ring-1 ring-gray-200"
        >
            <span className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
            Updating…
        </div>
    );
}

function LoadingSkeleton() {
    const skeletons = [
        { side: 'left', width: '55%' },
        { side: 'right', width: '45%' },
        { side: 'left', width: '65%' },
        { side: 'right', width: '35%' }
    ];

    return (
        <div className="flex flex-col flex-1 min-h-0 py-2 gap-3">
            {skeletons.map((s, i) => (
                <div
                    key={i}
                    className={`flex ${s.side === 'right' ? 'justify-end' : 'justify-start'}`}
                >
                    <div
                        className="h-10 bg-gray-200 rounded-2xl animate-pulse"
                        style={{ width: s.width }}
                    />
                </div>
            ))}
        </div>
    );
}

export { PatientMessagesLanding };
